type LabeledOption = {
  label: string;
};

type CompanyWithId = {
  company_id?: Number | string;
  uniqueId?: Number | string;
  company_name?: string;
};

const alphabeticalCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
});

export function sortOptionsAlphabetically<T extends LabeledOption>(
  options: readonly T[]
): T[] {
  return [...options].sort((left, right) =>
    alphabeticalCollator.compare(left.label, right.label)
  );
}

export function sortCompaniesByNumericId<T extends CompanyWithId>(
  companies: readonly T[]
): T[] {
  return [...companies].sort((left, right) => {
    const leftId = Number(left.company_id ?? left.uniqueId);
    const rightId = Number(right.company_id ?? right.uniqueId);
    const leftHasNumericId = Number.isFinite(leftId);
    const rightHasNumericId = Number.isFinite(rightId);

    if (leftHasNumericId && rightHasNumericId && leftId !== rightId) {
      return leftId - rightId;
    }
    if (leftHasNumericId !== rightHasNumericId) {
      return leftHasNumericId ? -1 : 1;
    }

    return alphabeticalCollator.compare(
      left.company_name ?? '',
      right.company_name ?? ''
    );
  });
}

type CompanyCollectionRow = {
  companies?: Record<string, unknown>;
  company_order?: readonly string[];
};

export function getOrderedCompanyNames(
  rows: readonly CompanyCollectionRow[]
): string[] {
  const explicitOrder = rows.find(
    (row) => row.company_order && row.company_order.length > 0
  )?.company_order;

  if (explicitOrder) return [...explicitOrder];

  return Array.from(
    new Set(rows.flatMap((row) => Object.keys(row.companies ?? {})))
  );
}
